import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * Which app a piece of help is written for.
 *
 * The two products ask different questions of the same feature — a consumer
 * wants to know why a transfer is pending, a business wants to know who on
 * the team can approve it. `all` is for the answers that genuinely do not
 * differ, so they are written once rather than drifting apart.
 */
export type HelpAudience = 'business' | 'consumer' | 'all';

export interface HelpCollection {
  id: string;
  slug: string;
  label: string;
  /**
   * An Iconsax (Bold) icon name, not a URL. The apps carry every glyph in
   * the set; a name they do not know draws as `Book1`.
   */
  icon: string;
  /** A palette role the app resolves against its theme — never a hex. */
  accent: string;
  audience: HelpAudience;
  sortOrder: number;
  archived: boolean;
}

export interface HelpArticleAction {
  label: string;
  /** An in-app path such as `/(auth)/invoices-list`. Never an external URL. */
  route: string;
}

export interface HelpArticle {
  id: string;
  slug: string;
  collectionId: string;
  collection?: HelpCollection;
  title: string;
  summary: string;
  readMinutes: number;
  /** Paragraphs. A leading `-` renders as a bullet in the app. Not markdown. */
  body: string[];
  action: HelpArticleAction | null;
  audience: HelpAudience;
  sortOrder: number;
  archived: boolean;
  /** Answers to "Did this answer your question?" — people and businesses
   *  together. Absent from an older server. */
  feedback?: { yes: number; no: number };
}

export interface UpsertHelpCollectionDto {
  slug?: string;
  label?: string;
  icon?: string;
  accent?: string;
  audience?: HelpAudience;
  sortOrder?: number;
  archived?: boolean;
}

export interface UpsertHelpArticleDto {
  slug?: string;
  collectionSlug?: string;
  title?: string;
  summary?: string;
  readMinutes?: number;
  body?: string[];
  action?: HelpArticleAction | null;
  audience?: HelpAudience;
  sortOrder?: number;
  archived?: boolean;
}

/** Palette roles, same list the app resolves. */
export const HELP_ACCENTS = [
  'info',
  'positive',
  'warning',
  'danger',
  'violet',
  'neutral',
] as const;

export const helpApi = {
  listCollections: async () => {
    const { data } = await apiClient.get<ApiResponse<HelpCollection[]>>(
      '/admin/help/collections',
    );
    return data.data;
  },

  createCollection: async (dto: UpsertHelpCollectionDto) => {
    const { data } = await apiClient.post<ApiResponse<HelpCollection>>(
      '/admin/help/collections',
      dto,
    );
    return data.data;
  },

  updateCollection: async (slug: string, dto: UpsertHelpCollectionDto) => {
    const { data } = await apiClient.patch<ApiResponse<HelpCollection>>(
      `/admin/help/collections/${encodeURIComponent(slug)}`,
      dto,
    );
    return data.data;
  },

  listArticles: async (collectionSlug?: string) => {
    const { data } = await apiClient.get<ApiResponse<HelpArticle[]>>(
      '/admin/help/articles',
      { params: collectionSlug ? { collection: collectionSlug } : undefined },
    );
    return data.data;
  },

  createArticle: async (dto: UpsertHelpArticleDto) => {
    const { data } = await apiClient.post<ApiResponse<HelpArticle>>(
      '/admin/help/articles',
      dto,
    );
    return data.data;
  },

  updateArticle: async (slug: string, dto: UpsertHelpArticleDto) => {
    const { data } = await apiClient.patch<ApiResponse<HelpArticle>>(
      `/admin/help/articles/${encodeURIComponent(slug)}`,
      dto,
    );
    return data.data;
  },
};
